/**
 * /table · ConciergeNoticed — soft cross-pillar awareness line.
 *
 * Surfaces on the results view ONLY when the parser detected
 * something interesting that another pillar can serve. We don't
 * route the member; we whisper an option.
 *
 * Triggers (priority order — first match wins):
 *   1. Two cities in the query  → "two cities? — your concierge
 *      can sketch a hop"  → /mobility deep-link
 *   2. Occasion word (anniversary | birthday | proposal | engagement
 *      | honeymoon)              → "we'll set the table with
 *      intention"  → opens the brief modal pre-tagged
 *   3. Travel intent (weekend | stay | trip | escape | holiday)
 *                                 → "an escape, too? — our /stay desk
 *      can find the room"  → /stay deep-link
 *
 * Doctrine: this NEVER hijacks the search. It's a quiet card below
 * the results header. Member can ignore it; that's fine.
 */
import React from "react";

const OCCASIONS = [
    "anniversary", "birthday", "proposal", "engagement",
    "honeymoon", "wedding",
];
const TRAVEL_INTENT = [
    "weekend", " stay", " trip", "escape", "holiday", "vacation",
    "long weekend",
];

const CITIES = [
    "bangkok", "bangalore", "bengaluru", "mumbai", "delhi", "new delhi",
    "kolkata", "chennai", "hyderabad", "pune", "goa",
    "jaipur", "udaipur",
    "paris", "london", "tokyo", "kyoto", "osaka", "lisbon",
    "barcelona", "madrid", "berlin", "vienna", "rome", "milan",
    "dubai", "abu dhabi", "doha", "istanbul", "singapore", "hong kong",
    "shanghai", "seoul", "sydney", "melbourne", "new york", "manhattan",
    "san francisco", "los angeles", "chicago", "miami", "boston",
];

function findCities(q) {
    const ql = (q || "").toLowerCase();
    const hits = [];
    for (const c of CITIES) {
        if (ql.includes(c)) hits.push(c);
    }
    // Deduplicate substring overlaps (e.g. "delhi" and "new delhi")
    const final = hits.filter(
        (a) => !hits.some((b) => b !== a && b.includes(a) && b.length > a.length),
    );
    return final.map((c) => c.split(" ").map(
        (w) => w[0].toUpperCase() + w.slice(1),
    ).join(" "));
}


export function ConciergeNoticed({ query, onMobility, onStay, onConcierge }) {
    const q = (query || "").trim();
    if (!q || q.length < 4) return null;

    const ql = q.toLowerCase();
    const cities = findCities(q);
    const occasion = OCCASIONS.find((w) => ql.includes(w));
    const travel = TRAVEL_INTENT.find((w) => ql.includes(w));

    let notice = null;

    if (cities.length >= 2) {
        notice = {
            kind: "two-cities",
            text: `${cities[0]} and ${cities[1]}? Your concierge can sketch the hop.`,
            cta: "Plan the move →",
            onClick: () => onMobility?.({ from: cities[0], to: cities[1] }),
        };
    } else if (occasion) {
        notice = {
            kind: "occasion",
            text: `An ${occasion[0] === "a" || occasion[0] === "e" ? "" : "a "}${occasion}. We'll set the table with intention.`,
            cta: "Tell us more →",
            onClick: () => onConcierge?.({ occasion }),
        };
    } else if (travel) {
        notice = {
            kind: "travel",
            text: `Sounds like a trip — the /stay desk knows the right room, too.`,
            cta: "Open /stay →",
            onClick: () => onStay?.({ q: cities[0] || q }),
        };
    }

    if (!notice) return null;

    return (
        <div
            data-testid="concierge-noticed"
            data-kind={notice.kind}
            className="t-fade"
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                background: "linear-gradient(to right, var(--t-brass-wash) 0%, var(--t-paper2) 70%)",
                border: "1px solid var(--t-brass2)",
                borderRadius: 14,
                padding: "12px 16px",
                marginBottom: 14,
            }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                <span
                    className="t-mono uppercase"
                    style={{
                        fontSize: 10,
                        letterSpacing: "0.2em",
                        color: "var(--t-brass)",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                    }}
                >
                    Concierge® noticed
                </span>
                <span
                    className="t-serif"
                    style={{
                        fontSize: 14,
                        color: "var(--t-ink)",
                        fontStyle: "italic",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {notice.text}
                </span>
            </div>
            <button
                type="button"
                data-testid={`concierge-noticed-cta-${notice.kind}`}
                onClick={notice.onClick}
                style={{
                    background: "var(--t-ink)",
                    color: "var(--t-paper)",
                    border: 0,
                    borderRadius: 999,
                    padding: "7px 16px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                    letterSpacing: "0.02em",
                    flexShrink: 0,
                    transition: "transform 120ms ease",
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-1px)"}
                onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
            >
                {notice.cta}
            </button>
        </div>
    );
}
